import { skipToken } from '@reduxjs/toolkit/query';
import CircularLoader from 'components/CircularLoader';
import EmptyState from 'components/EmptyState';
import {
    CheckIcon,
    DescriptionOutlined as DescriptionOutlinedIcon,
    PlusOutlined,
    SearchOutlined,
} from 'components/icons';
import { Button } from 'components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Toggle } from 'components/ui/toggle';
import { ReactNode, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useGetMatterTimecardTemplatesQuery } from 'store/api/timecard-templates';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { CallForActionButton, CallForActionButtonTooltip } from './CallForActionButton';
import { addedCount, filterGroups, groupTemplates, listedGroups, revealsMore } from './timecardTemplateGroups';

export type TemplateApplyMode = 'add' | 'replace';

type Props = {
    matterId?: number | null;
    /** The case's `timecardLanguageCode`: its templates are the ones listed before an "Also show" chip is pressed. */
    matterLanguageCode?: string | null;
    onSelect: (templateText: string, mode: TemplateApplyMode) => void;
};

function LinkButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
    return (
        <Button variant="link" className="text-link h-auto p-0 text-xs font-normal" onClick={onClick}>
            {children}
        </Button>
    );
}

export default function TimecardTemplateButton({ matterId, matterLanguageCode, onSelect }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [showOtherLanguages, setShowOtherLanguages] = useState(false);
    const [showOtherTypes, setShowOtherTypes] = useState(false);
    const [search, setSearch] = useState('');
    const [openedForMatterId, setOpenedForMatterId] = useState<number | null>(null);
    const { classifiers } = useClassifiers({ codes: ['LANGUAGE', 'MATTER_TYPE'] });
    // Selecting another case forgets the opening, so coming back to an opened case does not fetch on selection
    // alone (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    if (openedForMatterId != null && openedForMatterId !== matterId) {
        setOpenedForMatterId(null);
    }

    // `currentData`, not `data`: RTK keeps the previous case's rows in `data` while the next case's are in
    // flight, and the own type is read off these rows.
    const { currentData: ownTemplates, isFetching: isFetchingOwn } = useGetMatterTimecardTemplatesQuery(
        matterId != null ? { matterId, includeOtherMatterTypes: false } : skipToken
    );
    // Fetched from the case's first opening rather than on its selection, then kept subscribed while the
    // case stays selected so a reopen does not wait on it again. The other-type chip can only show once it is in.
    const { currentData: allTemplates, isFetching: isFetchingAll } = useGetMatterTimecardTemplatesQuery(
        matterId != null && openedForMatterId === matterId ? { matterId, includeOtherMatterTypes: true } : skipToken
    );
    const isLoading = (isFetchingOwn && !ownTemplates) || (isFetchingAll && !allTemplates);

    const groups = useMemo(
        () =>
            groupTemplates(ownTemplates ?? [], allTemplates, matterLanguageCode, {
                matterType: (code) => getClassifierText(classifiers['MATTER_TYPE'], code),
                language: (code) => getClassifierText(classifiers['LANGUAGE'], code),
            }),
        [ownTemplates, allTemplates, matterLanguageCode, classifiers]
    );
    const expansion = { otherLanguages: showOtherLanguages, otherTypes: showOtherTypes };
    const listed = listedGroups(groups, expansion);
    const shown = filterGroups(listed, search);
    // A chip shows while pressed or while pressing it would list more; the search is left out so chips do not
    // come and go as the user types.
    const chips = (
        [
            ['otherLanguages', 'timecards.templates.other-languages', setShowOtherLanguages],
            ['otherTypes', 'timecards.templates.other-matter-types', setShowOtherTypes],
        ] as const
    ).filter(([axis]) => expansion[axis] || revealsMore(groups, expansion, { [axis]: true }));
    const chipsLabelId = useId();

    // Pressing a chip scrolls the first group it adds to the top: other case types sort after the own type, so
    // without it a press can look like it did nothing.
    const listRef = useRef<HTMLDivElement>(null);
    const keysBeforePress = useRef<string[] | null>(null);
    const press = (set: (on: boolean) => void) => (on: boolean) => {
        if (on) keysBeforePress.current = shown.map((group) => group.key);
        set(on);
    };
    useLayoutEffect(() => {
        const before = keysBeforePress.current;
        if (!before) return;
        keysBeforePress.current = null;
        const added = shown.find((group) => !before.includes(group.key));
        const list = listRef.current;
        const element = added && list?.querySelector<HTMLElement>(`[data-group-key="${added.key}"]`);
        if (list && element) list.scrollTop = element.offsetTop;
    }, [shown]);

    const apply = (templateText: string, mode: TemplateApplyMode) => {
        onSelect(templateText, mode);
        setIsOpen(false);
    };

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                // Each opening starts collapsed and unfiltered: the case's own type in its language is
                // what the popup is for, and the expansion and the filter are choices about the list
                // being shown — not ones that should carry over to the next case selected in the form.
                if (open) {
                    setShowOtherLanguages(false);
                    setShowOtherTypes(false);
                    setSearch('');
                    setOpenedForMatterId(matterId ?? null);
                }
                setIsOpen(open);
            }}
        >
            <CallForActionButtonTooltip title={t('timecards.templates.templates')}>
                <PopoverTrigger
                    render={
                        <CallForActionButton
                            variant="contained"
                            shape="rounded"
                            color="info"
                            aria-label={t('timecards.templates.templates')}
                        >
                            <DescriptionOutlinedIcon />
                        </CallForActionButton>
                    }
                />
            </CallForActionButtonTooltip>
            <PopoverContent
                align="end"
                sideOffset={8}
                // Opened from inside the tracker drawer and modals, so it stacks above them.
                positionerClassName="z-(--z-tooltip)"
                className="border-divider w-[560px] max-w-[calc(100vw-2rem)] gap-0 rounded border p-0 shadow-xl ring-0"
            >
                {isLoading ? (
                    <CircularLoader className="static p-4" />
                ) : (
                    <>
                        {listed.length > 0 || chips.length > 0 ? (
                            <div className="border-divider flex flex-col gap-2 border-b px-4 py-2">
                                {listed.length > 0 ? (
                                    <InputGroup className="bg-paper">
                                        <InputGroupAddon>
                                            <SearchOutlined className="text-grey-500" />
                                        </InputGroupAddon>
                                        <InputGroupInput
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder={t('timecards.templates.search-placeholder')}
                                            aria-label={t('timecards.templates.search-placeholder')}
                                        />
                                    </InputGroup>
                                ) : null}
                                {chips.length > 0 ? (
                                    <div
                                        role="group"
                                        aria-labelledby={chipsLabelId}
                                        className="flex flex-wrap items-center gap-2"
                                    >
                                        <span id={chipsLabelId} className="text-grey-600 text-xs">
                                            {t('timecards.templates.also-show')}
                                        </span>
                                        {chips.map(([axis, labelId, set]) => (
                                            <Toggle
                                                key={axis}
                                                variant="outline"
                                                size="sm"
                                                className="aria-pressed:border-primary aria-pressed:bg-accent aria-pressed:text-primary h-6 min-w-0 gap-1 px-2 text-xs"
                                                pressed={expansion[axis]}
                                                onPressedChange={press(set)}
                                            >
                                                {expansion[axis] ? (
                                                    <CheckIcon className="size-3" />
                                                ) : (
                                                    <PlusOutlined className="size-3" />
                                                )}
                                                {t(labelId)}
                                                <span className="font-normal tabular-nums">
                                                    {addedCount(groups, expansion, axis, search)}
                                                </span>
                                            </Toggle>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <div ref={listRef} className="relative max-h-[325px] overflow-y-auto">
                            {listed.length === 0 ? (
                                <EmptyState
                                    size="inline"
                                    titleId="timecards.templates.no-records"
                                    surface="timecard-templates"
                                />
                            ) : shown.length === 0 ? (
                                <EmptyState
                                    size="inline"
                                    variant="filtered"
                                    onClearFilters={() => setSearch('')}
                                    surface="timecard-templates"
                                />
                            ) : (
                                shown.map((group) => (
                                    <div key={group.key} data-group-key={group.key}>
                                        <div className="bg-grey-50 border-divider text-grey-600 sticky top-0 z-1 border-b px-4 py-2 text-xs font-medium">
                                            {t('timecards.templates.group-title', {
                                                language: group.language,
                                                matterType: group.matterType,
                                            })}
                                        </div>
                                        {group.templates.map((template) => (
                                            <div
                                                key={template.id}
                                                className="border-divider flex items-start gap-2 border-b px-4 py-2 last:border-b-0"
                                            >
                                                {/* Templates are stored with their line breaks; `pre-wrap` is what shows them. */}
                                                <div className="grow text-sm whitespace-pre-wrap">
                                                    {template.templateText}
                                                </div>
                                                <div className="flex shrink-0 gap-3">
                                                    <LinkButton onClick={() => apply(template.templateText, 'add')}>
                                                        {t('add')}
                                                    </LinkButton>
                                                    <LinkButton onClick={() => apply(template.templateText, 'replace')}>
                                                        {t('replace')}
                                                    </LinkButton>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </PopoverContent>
        </Popover>
    );
}
